import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    HiArrowLeft,
    HiOutlineSearch,
    HiOutlineX,
    HiOutlineShoppingBag,
    HiOutlineChevronDown,
    HiOutlineChevronUp,
    HiOutlineCalendar,
    HiOutlineClock,
    HiOutlineClipboardCheck,
    HiOutlineGift,
    HiOutlineInformationCircle,
    HiOutlineLightningBolt,
    HiOutlineExclamation,
    HiOutlineTag,
    HiX,
    HiOutlineCube,
    HiOutlineLink,
    HiOutlinePencil
} from "react-icons/hi";
import { HiTrophy } from "react-icons/hi2";
import "./SingleMemberDetails.css";
import Button from "../shared/Button";
import TextInput from "../shared/TextInput";
import Card from "../shared/Card";
import Badge from "../shared/Badge";
import Modal from "../shared/Modal";
import { cn } from "../../utils/cn";
import { buildMemberGroupMap, formatMemberWithGroups } from "../../utils/memberGroups";
import { formatPrice } from "../../utils/currency";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { updateMemberNameAction } from "../../redux/actions/memberActions";

const SingleMemberDetails = ({ member, campaigns, integrations, onCampaignClick, onMemberClose }) => {
    const dispatch = useDispatch();
    const isAdmin = useIsAdmin();
    const organisationId = useSelector((state) => state.organisation?.currentOrganisation?._id);
    const currency = useSelector((state) => state.organisation?.currentOrganisation?.settings?.currency || 'EUR');
    const groups = useSelector((state) => state.organisation?.currentOrganisation?.groups);
    const memberGroupMap = useMemo(() => buildMemberGroupMap(groups), [groups]);
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedItems, setExpandedItems] = useState({});
    const [activeTab, setActiveTab] = useState("transactions");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showActivity, setShowActivity] = useState(false);
    const [showEditNameModal, setShowEditNameModal] = useState(false);
    const [nameDraft, setNameDraft] = useState("");
    const [isSavingName, setIsSavingName] = useState(false);
    const [editNameError, setEditNameError] = useState("");
    const popupRef = useRef(null);
    const searchInputRef = useRef(null);

    const openEditNameModal = () => {
        setNameDraft(member?.name || "");
        setEditNameError("");
        setShowEditNameModal(true);
    };

    const closeEditNameModal = () => {
        if (isSavingName) return;
        setShowEditNameModal(false);
        setEditNameError("");
    };

    const handleSaveName = async () => {
        const trimmed = nameDraft.trim();
        if (!trimmed) {
            setEditNameError("Name is required");
            return;
        }
        if (trimmed === (member?.name || "")) {
            setShowEditNameModal(false);
            return;
        }
        setIsSavingName(true);
        setEditNameError("");
        try {
            await dispatch(updateMemberNameAction(organisationId, member._id, trimmed));
            setShowEditNameModal(false);
        } catch (err) {
            setEditNameError(err.message || "Failed to update name");
        } finally {
            setIsSavingName(false);
        }
    };

    const handleSearch = (event) => setSearchTerm(event.target.value);
    const clearSearch = () => {
        setSearchTerm("");
        searchInputRef.current?.focus();
    };

    const getCampaignDetails = (campaignId) =>
        campaigns?.find((campaign) => campaign._id === campaignId);

    const getIntegrationDetails = (integrationId) =>
        integrations?.find((integration) => integration._id === integrationId);

    const isIntegrationTransaction = (transaction) =>
        transaction.source === "integration" || transaction.integrationId;

    const getTransactionSourceName = (transaction) => {
        if (isIntegrationTransaction(transaction)) {
            const integration = getIntegrationDetails(transaction.integrationId);
            return integration?.name || transaction.description || "Integration";
        }
        const campaign = getCampaignDetails(transaction.campaignId);
        return campaign?.name || "Unknown Campaign";
    };

    const toggleExpand = (itemId) => {
        setExpandedItems((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
    };

    const closeProductView = () => setSelectedProduct(null);

    const filteredTransactions =
        member.transactions?.filter((transaction) => {
            const searchLower = searchTerm.toLowerCase();
            if (isIntegrationTransaction(transaction)) {
                const integration = getIntegrationDetails(transaction.integrationId);
                const searchableText = `${integration?.name || ""} ${transaction.description || ""} ${transaction.contractNumber || ""}`.toLowerCase();
                return searchableText.includes(searchLower);
            }
            const campaign = getCampaignDetails(transaction.campaignId);
            const searchableText = `${transaction.campaignId} ${campaign?.name || ""}`.toLowerCase();
            return searchableText.includes(searchLower);
        }) || [];

    const filteredRedeemedPoints =
        member.redeemedPoints?.filter((redeemedPoint) =>
            redeemedPoint.orderId?.toLowerCase().includes(searchTerm.toLowerCase())
        ) || [];

    const getAvatarLetter = (m) =>
        m.name
            ? m.name[0].toUpperCase()
            : m.identification
            ? m.identification[0].toUpperCase()
            : "?";

    const getRedeemedPoints = () =>
        member.redeemedPoints?.reduce((sum, rp) => sum + (rp.points || 0), 0) || 0;

    // Splits an order's total redeemed points across its product lines proportionally
    // to line value (price × quantity), using largest-remainder rounding so the parts
    // sum back to the order total. Returns an array of integers aligned with products[].
    const computeProductPoints = (redeemedPoint) => {
        const products = redeemedPoint.products || [];
        const totalPoints = redeemedPoint.points || 0;
        const lineTotals = products.map(
            (p) => Number(p.price || 0) * Number(p.quantity || 0)
        );
        const orderTotal = lineTotals.reduce((s, v) => s + v, 0);
        if (!totalPoints || !orderTotal) return products.map(() => 0);
        const raw = lineTotals.map((lt) => (totalPoints * lt) / orderTotal);
        const floored = raw.map(Math.floor);
        const remainder = totalPoints - floored.reduce((s, v) => s + v, 0);
        const order = raw
            .map((v, i) => ({ i, frac: v - Math.floor(v) }))
            .sort((a, b) => b.frac - a.frac);
        for (let k = 0; k < remainder; k++) floored[order[k].i] += 1;
        return floored;
    };

    const getTransactionCount = () => member.transactions?.length || 0;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                closeProductView();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        setShowActivity(false);
    }, [activeTab]);

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        });
    };

    const groupTransactionsByDate = () => {
        const grouped = {};
        filteredTransactions.forEach((transaction) => {
            if (!transaction.timestamp) return;
            const date = new Date(transaction.timestamp);
            const dateStr = date.toISOString().split("T")[0];
            if (!grouped[dateStr]) grouped[dateStr] = [];
            grouped[dateStr].push(transaction);
        });
        return Object.entries(grouped)
            .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
            .map(([date, transactions]) => ({ date, transactions }));
    };

    const tabClass = (tab) =>
        cn(
            "flex-1 py-4 px-6 font-medium text-center tab-button transition-colors",
            activeTab === tab
                ? "text-ink border-b-2 border-ink"
                : "text-muted border-b-2 border-transparent hover:text-ink"
        );

    return (
        <div className="member-details-container min-h-screen bg-canvas">
            {/* Product Popup */}
            {selectedProduct && (
                <div className="fixed inset-0 bg-ink/50 flex justify-center items-center z-50 p-4 modal-overlay">
                    <div
                        ref={popupRef}
                        className="rounded-lg shadow-card p-6 w-full max-w-md modal-content bg-canvas border border-hairline"
                    >
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-title-md text-ink font-medium">
                                {selectedProduct.name}
                            </h2>
                            <button
                                onClick={closeProductView}
                                className="text-muted hover:text-ink transition-colors"
                            >
                                <HiX className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="rounded-md overflow-hidden mb-4 bg-surface-soft">
                            <img
                                src={selectedProduct.imageUrl}
                                alt={selectedProduct.name}
                                className="w-full h-56 object-cover"
                            />
                        </div>

                        <div className="space-y-4">
                            <p className="text-body-md text-body">{selectedProduct.description}</p>

                            <div className="flex flex-wrap gap-4">
                                <div className="rounded-md px-4 py-2 flex-1 stat-card bg-surface-soft">
                                    <p className="text-[12px] text-muted">Quantity</p>
                                    <p className="text-title-sm font-medium text-ink">
                                        {selectedProduct.quantity}
                                    </p>
                                </div>

                                <div className="rounded-md px-4 py-2 flex-1 stat-card bg-surface-soft">
                                    <p className="text-[12px] text-muted">Price</p>
                                    <p className="text-title-sm font-medium text-ink">
                                        {formatPrice(selectedProduct.price, currency)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                {/* Back Button */}
                <button
                    onClick={onMemberClose}
                    className="flex items-center text-muted hover:text-ink transition-colors back-button mb-6"
                >
                    <HiArrowLeft className="w-5 h-5 mr-2" />
                    <span className="font-medium">Back to Members</span>
                </button>

                <div className="member-details-card rounded-lg overflow-hidden border border-hairline bg-canvas">
                    {/* Member Profile Header */}
                    <div className="relative">
                        {/* Header background — flat ink band */}
                        <div className="h-32 w-full bg-surface-dark" />

                        {/* Profile Content */}
                        <div className="px-6 pb-6 relative z-10">
                            <div className="flex flex-col md:flex-row items-start md:items-end -mt-6 gap-6">
                                {/* Avatar */}
                                <div className="h-24 w-24 rounded-md flex items-center justify-center text-ink text-display-md font-medium shadow-card member-avatar border border-hairline bg-canvas overflow-hidden">
                                    {getAvatarLetter(member)}
                                </div>

                                {/* Member Name and Status */}
                                <div className="flex-1 mt-4 md:mt-0">
                                    <div className="flex flex-wrap items-start md:items-center gap-3">
                                        <h1 className="text-display-md text-ink">
                                            {formatMemberWithGroups(member, memberGroupMap, "Unknown Member")}
                                        </h1>
                                        {isAdmin && (
                                            <button
                                                onClick={openEditNameModal}
                                                className="p-1.5 rounded-md text-muted hover:text-ink hover:bg-surface-soft transition-colors"
                                                title="Edit name"
                                                aria-label="Edit member name"
                                            >
                                                <HiOutlinePencil className="h-5 w-5" />
                                            </button>
                                        )}
                                        {member.status && (
                                            <Badge
                                                variant={member.status === "active" ? "success" : "warning"}
                                            >
                                                <span
                                                    className={`activity-indicator ${
                                                        member.status === "active"
                                                            ? "bg-signature-forest"
                                                            : "bg-signature-mustard"
                                                    }`}
                                                />
                                                {member.status === "active" ? "Active" : "Inactive"}
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-body-md text-muted mt-1">
                                        {member.identification || "No ID provided"}
                                    </p>
                                </div>

                                {/* Quick Stats */}
                                <div className="flex flex-wrap gap-3 mt-4 md:mt-0">
                                    <div className="py-2 px-4 rounded-md flex flex-col items-center bg-surface-soft border border-hairline">
                                        <div className="text-title-md text-ink font-medium">
                                            {member.points || 0}
                                        </div>
                                        <div className="text-[12px] uppercase tracking-wide text-muted">
                                            Points
                                        </div>
                                    </div>

                                    <div className="py-2 px-4 rounded-md flex flex-col items-center bg-signature-cream">
                                        <div className="text-title-md text-ink font-medium">
                                            {getRedeemedPoints()}
                                        </div>
                                        <div className="text-[12px] uppercase tracking-wide text-ink">
                                            Redeemed
                                        </div>
                                    </div>

                                    <div className="py-2 px-4 rounded-md flex flex-col items-center bg-surface-soft border border-hairline">
                                        <div className="text-title-md text-ink font-medium">
                                            {getTransactionCount()}
                                        </div>
                                        <div className="text-[12px] uppercase tracking-wide text-muted">
                                            Transactions
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex border-b border-hairline">
                        <button className={tabClass("transactions")} onClick={() => setActiveTab("transactions")}>
                            <div className="flex items-center justify-center gap-2">
                                <HiOutlineShoppingBag className="w-5 h-5" />
                                <span>Transactions ({member.transactions?.length || 0})</span>
                            </div>
                        </button>
                        <button className={tabClass("redeemedPoints")} onClick={() => setActiveTab("redeemedPoints")}>
                            <div className="flex items-center justify-center gap-2">
                                <HiOutlineGift className="w-5 h-5" />
                                <span>Redeemed Points ({member.redeemedPoints?.length || 0})</span>
                            </div>
                        </button>
                        <button className={tabClass("activity")} onClick={() => setActiveTab("activity")}>
                            <div className="flex items-center justify-center gap-2">
                                <HiOutlineClipboardCheck className="w-5 h-5" />
                                <span>Activity Timeline</span>
                            </div>
                        </button>
                    </div>

                    {/* Search */}
                    <div className="p-6 border-b border-hairline flex justify-between items-center flex-wrap gap-4">
                        <div className="flex-grow">
                            <TextInput
                                ref={searchInputRef}
                                type="text"
                                placeholder={
                                    activeTab === "transactions"
                                        ? "Search transactions..."
                                        : activeTab === "redeemedPoints"
                                        ? "Search by order ID..."
                                        : "Search activity..."
                                }
                                value={searchTerm}
                                onChange={handleSearch}
                                leadingIcon={<HiOutlineSearch className="h-5 w-5" />}
                                trailingIcon={
                                    searchTerm ? (
                                        <button onClick={clearSearch} aria-label="Clear">
                                            <HiOutlineX className="h-5 w-5" />
                                        </button>
                                    ) : null
                                }
                            />
                        </div>

                        {activeTab !== "activity" && (
                            <Button
                                variant={showActivity ? "primary" : "secondary"}
                                size="sm"
                                onClick={() => setShowActivity(!showActivity)}
                                leadingIcon={
                                    showActivity ? (
                                        <HiOutlineX className="h-4 w-4" />
                                    ) : (
                                        <HiOutlineCalendar className="h-4 w-4" />
                                    )
                                }
                            >
                                {showActivity ? "Hide Overview" : "Show Overview"}
                            </Button>
                        )}
                    </div>

                    {/* Content Area */}
                    <div className="p-6">
                        {/* Activity Overview */}
                        {showActivity && activeTab !== "activity" && (
                            <Card variant="surface-soft" className="mb-8 !p-4">
                                <h3 className="text-title-sm text-ink mb-4">Activity Overview</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {groupTransactionsByDate()
                                        .slice(0, 3)
                                        .map((group, idx) => (
                                            <div
                                                key={idx}
                                                className="rounded-md border border-hairline p-3 bg-canvas"
                                            >
                                                <div className="flex items-center gap-2 mb-2">
                                                    <HiOutlineCalendar className="h-4 w-4 text-ink" />
                                                    <span className="text-body-md font-medium text-ink">
                                                        {new Date(group.date).toLocaleDateString("en-US", {
                                                            month: "short",
                                                            day: "numeric",
                                                            year: "numeric"
                                                        })}
                                                    </span>
                                                </div>
                                                <div className="text-body-md text-muted">
                                                    {group.transactions.length}{" "}
                                                    {group.transactions.length === 1 ? "transaction" : "transactions"}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </Card>
                        )}

                        {/* Transactions Tab */}
                        {activeTab === "transactions" && (
                            <div>
                                {filteredTransactions.length === 0 ? (
                                    <div className="text-center py-12">
                                        <div className="mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-4 bg-surface-soft">
                                            <HiOutlineShoppingBag className="h-6 w-6 text-muted" />
                                        </div>
                                        <h3 className="text-title-sm text-ink mb-1">No transactions found</h3>
                                        <p className="text-body-md text-muted">
                                            {searchTerm
                                                ? "Try adjusting your search term"
                                                : "This member has no transactions yet"}
                                        </p>
                                    </div>
                                ) : (
                                    <ul className="space-y-3">
                                        {filteredTransactions.map((transaction, index) => {
                                            const isIntegration = isIntegrationTransaction(transaction);
                                            const campaignDetails = !isIntegration
                                                ? getCampaignDetails(transaction.campaignId)
                                                : null;
                                            const integrationDetails = isIntegration
                                                ? getIntegrationDetails(transaction.integrationId)
                                                : null;
                                            const itemKey = `transaction-${index}`;
                                            const isExpanded = expandedItems[itemKey];

                                            return (
                                                <li
                                                    key={index}
                                                    className="border border-hairline rounded-md transaction-item overflow-hidden bg-canvas"
                                                >
                                                    <div
                                                        className="p-4 cursor-pointer hover:bg-surface-soft transition-colors"
                                                        onClick={() => toggleExpand(itemKey)}
                                                    >
                                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                                            <div>
                                                                <div className="flex items-center gap-2">
                                                                    {isIntegration ? (
                                                                        <HiOutlineLink className="h-5 w-5 text-ink" />
                                                                    ) : (
                                                                        <HiOutlineShoppingBag className="h-5 w-5 text-ink" />
                                                                    )}
                                                                    <p className="text-label-md font-medium text-ink">
                                                                        {getTransactionSourceName(transaction)}
                                                                    </p>
                                                                    {isIntegration ? (
                                                                        <Badge
                                                                            variant={
                                                                                integrationDetails?.isEnabled
                                                                                    ? "success"
                                                                                    : "warning"
                                                                            }
                                                                            size="sm"
                                                                        >
                                                                            Integration
                                                                        </Badge>
                                                                    ) : (
                                                                        campaignDetails && (
                                                                            <Badge
                                                                                variant={
                                                                                    campaignDetails.status === "running"
                                                                                        ? "success"
                                                                                        : "warning"
                                                                                }
                                                                                size="sm"
                                                                            >
                                                                                {campaignDetails.status === "running"
                                                                                    ? "Active"
                                                                                    : "Inactive"}
                                                                            </Badge>
                                                                        )
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-2 mt-1 text-body-md text-muted">
                                                                    <HiOutlineClock className="h-4 w-4" />
                                                                    <p>{formatDate(transaction.timestamp)}</p>
                                                                    {isIntegration && transaction.contractNumber && (
                                                                        <>
                                                                            <span>•</span>
                                                                            <p className="font-mono">
                                                                                #{transaction.contractNumber}
                                                                            </p>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="mt-3 sm:mt-0 flex items-center gap-3">
                                                                <Badge variant="success">
                                                                    +{transaction.value || transaction.points || 0} pts
                                                                </Badge>

                                                                <button className="text-body-md font-medium flex items-center gap-1 text-muted hover:text-ink">
                                                                    Details
                                                                    {isExpanded ? (
                                                                        <HiOutlineChevronUp className="h-5 w-5" />
                                                                    ) : (
                                                                        <HiOutlineChevronDown className="h-5 w-5" />
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className={`detail-panel ${isExpanded ? "expanded" : ""}`}>
                                                        {isExpanded && (
                                                            <div className="border-t border-hairline p-4">
                                                                {isIntegration ? (
                                                                    <>
                                                                        <h4 className="text-title-sm text-ink mb-3">
                                                                            Integration Details
                                                                        </h4>
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                            <div className="p-3 rounded-md bg-surface-soft">
                                                                                <div className="flex items-center gap-2 mb-2">
                                                                                    <HiOutlineLink className="h-4 w-4 text-ink" />
                                                                                    <p className="text-[12px] font-medium text-muted">
                                                                                        Integration
                                                                                    </p>
                                                                                </div>
                                                                                <p className="text-body-md font-medium text-ink">
                                                                                    {integrationDetails?.name || "Unknown Integration"}
                                                                                </p>
                                                                                {integrationDetails?.description && (
                                                                                    <p className="text-[12px] mt-1 text-muted">
                                                                                        {integrationDetails.description}
                                                                                    </p>
                                                                                )}
                                                                            </div>

                                                                            {transaction.contractNumber && (
                                                                                <div className="p-3 rounded-md bg-surface-soft">
                                                                                    <div className="flex items-center gap-2 mb-2">
                                                                                        <HiOutlineTag className="h-4 w-4 text-ink" />
                                                                                        <p className="text-[12px] font-medium text-muted">
                                                                                            Contract Number
                                                                                        </p>
                                                                                    </div>
                                                                                    <p className="text-body-md font-mono text-ink">
                                                                                        {transaction.contractNumber}
                                                                                    </p>
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        {transaction.additionalProperties &&
                                                                            Object.keys(transaction.additionalProperties).length > 0 && (
                                                                                <div className="mt-4">
                                                                                    <div className="flex items-center gap-2 mb-2">
                                                                                        <HiOutlineCube className="h-4 w-4 text-ink" />
                                                                                        <p className="text-body-md font-medium text-muted">
                                                                                            Additional Data
                                                                                        </p>
                                                                                    </div>
                                                                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                                                                                        {Object.entries(transaction.additionalProperties).map(
                                                                                            ([key, value]) => (
                                                                                                <div
                                                                                                    key={key}
                                                                                                    className="p-2 rounded-md text-body-md bg-surface-soft text-ink"
                                                                                                >
                                                                                                    <span className="text-muted">{key}: </span>
                                                                                                    <span className="font-medium">
                                                                                                        {String(value)}
                                                                                                    </span>
                                                                                                </div>
                                                                                            )
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                        <div className="mt-4 p-3 rounded-md bg-signature-cream">
                                                                            <div className="flex items-center gap-2">
                                                                                <HiTrophy className="h-5 w-5 text-ink" />
                                                                                <p className="font-medium text-ink">
                                                                                    +{transaction.value || 0} points awarded
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    campaignDetails && (
                                                                        <>
                                                                            <h4 className="text-title-sm text-ink mb-3">
                                                                                Campaign Details
                                                                            </h4>
                                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                                <div className="p-3 rounded-md bg-surface-soft">
                                                                                    <div className="flex items-center gap-2 mb-2">
                                                                                        <HiOutlineTag className="h-4 w-4 text-ink" />
                                                                                        <p className="text-[12px] font-medium text-muted">
                                                                                            Campaign ID
                                                                                        </p>
                                                                                    </div>
                                                                                    <p className="text-body-md font-mono text-ink">
                                                                                        {campaignDetails._id}
                                                                                    </p>
                                                                                </div>

                                                                                {campaignDetails.fraudPrevention && (
                                                                                    <div className="p-3 rounded-md bg-surface-soft">
                                                                                        <div className="flex items-center gap-2 mb-2">
                                                                                            <HiOutlineExclamation className="h-4 w-4 text-ink" />
                                                                                            <p className="text-[12px] font-medium text-muted">
                                                                                                Fraud Prevention
                                                                                            </p>
                                                                                        </div>
                                                                                        <div className="grid grid-cols-2 gap-2">
                                                                                            <div>
                                                                                                <p className="text-[12px] text-muted">
                                                                                                    Max Points
                                                                                                </p>
                                                                                                <p className="text-body-md font-medium text-ink">
                                                                                                    {campaignDetails.fraudPrevention.maxPoints || "N/A"}
                                                                                                </p>
                                                                                            </div>
                                                                                            <div>
                                                                                                <p className="text-[12px] text-muted">
                                                                                                    Max Transactions
                                                                                                </p>
                                                                                                <p className="text-body-md font-medium text-ink">
                                                                                                    {campaignDetails.fraudPrevention.maxTransactions || "N/A"}
                                                                                                </p>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>

                                                                            {campaignDetails.conditions?.length > 0 && (
                                                                                <div className="mt-4">
                                                                                    <div className="flex items-center gap-2 mb-2">
                                                                                        <HiOutlineLightningBolt className="h-4 w-4 text-ink" />
                                                                                        <p className="text-body-md font-medium text-muted">
                                                                                            Conditions
                                                                                        </p>
                                                                                    </div>
                                                                                    <ul className="space-y-2">
                                                                                        {campaignDetails.conditions.map((condition, i) => (
                                                                                            <li
                                                                                                key={i}
                                                                                                className="p-2 rounded-md text-body-md bg-surface-soft text-ink"
                                                                                            >
                                                                                                {condition.type === "transaction"
                                                                                                    ? `${condition.transactionBase} ${condition.transactionOperator} ${condition.inputValue} ${condition.valueMetric}`
                                                                                                    : JSON.stringify(condition)}
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                </div>
                                                                            )}

                                                                            {campaignDetails.rewards?.length > 0 && (
                                                                                <div className="mt-4">
                                                                                    <div className="flex items-center gap-2 mb-2">
                                                                                        <HiTrophy className="h-4 w-4 text-ink" />
                                                                                        <p className="text-body-md font-medium text-muted">
                                                                                            Rewards
                                                                                        </p>
                                                                                    </div>
                                                                                    <ul className="space-y-2">
                                                                                        {campaignDetails.rewards.map((reward, i) => (
                                                                                            <li
                                                                                                key={i}
                                                                                                className="p-2 rounded-md text-body-md bg-signature-cream text-ink"
                                                                                            >
                                                                                                {`Add ${reward.points} to ${reward.rewardBase}`}
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                </div>
                                                                            )}

                                                                            <div className="mt-4 flex justify-end">
                                                                                <Button
                                                                                    variant="primary"
                                                                                    size="sm"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        onCampaignClick && onCampaignClick(campaignDetails);
                                                                                    }}
                                                                                    leadingIcon={
                                                                                        <HiOutlineInformationCircle className="h-4 w-4" />
                                                                                    }
                                                                                >
                                                                                    View Campaign
                                                                                </Button>
                                                                            </div>
                                                                        </>
                                                                    )
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        )}

                        {/* Redeemed Points Tab */}
                        {activeTab === "redeemedPoints" && (
                            <div>
                                {filteredRedeemedPoints.length === 0 ? (
                                    <div className="text-center py-12">
                                        <div className="mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-4 bg-surface-soft">
                                            <HiOutlineGift className="h-6 w-6 text-muted" />
                                        </div>
                                        <h3 className="text-title-sm text-ink mb-1">No redeemed points found</h3>
                                        <p className="text-body-md text-muted">
                                            {searchTerm
                                                ? "Try adjusting your search term"
                                                : "This member has not redeemed any points yet"}
                                        </p>
                                    </div>
                                ) : (
                                    <ul className="space-y-3">
                                        {filteredRedeemedPoints.map((redeemedPoint, index) => {
                                            const itemKey = `redeemed-${index}`;
                                            const isExpanded = expandedItems[itemKey];
                                            const productPoints = computeProductPoints(redeemedPoint);

                                            return (
                                                <li
                                                    key={index}
                                                    className="border border-hairline rounded-md transaction-item overflow-hidden bg-canvas"
                                                >
                                                    <div
                                                        className="p-4 cursor-pointer hover:bg-surface-soft transition-colors"
                                                        onClick={() => toggleExpand(itemKey)}
                                                    >
                                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                                            <div>
                                                                <p className="text-label-md font-medium text-ink">
                                                                    Order #{redeemedPoint.orderId}
                                                                </p>
                                                                <div className="flex items-center gap-2 mt-1 text-body-md text-muted">
                                                                    <HiOutlineClock className="h-4 w-4" />
                                                                    <p>{formatDate(redeemedPoint.timestamp)}</p>
                                                                </div>
                                                            </div>

                                                            <div className="mt-3 sm:mt-0 flex items-center gap-3">
                                                                <Badge variant="cream">
                                                                    {redeemedPoint.points} points
                                                                </Badge>

                                                                <button className="text-body-md font-medium flex items-center gap-1 text-muted hover:text-ink">
                                                                    {redeemedPoint.products?.length ? "Products" : "Details"}
                                                                    {isExpanded ? (
                                                                        <HiOutlineChevronUp className="h-5 w-5" />
                                                                    ) : (
                                                                        <HiOutlineChevronDown className="h-5 w-5" />
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className={`detail-panel ${isExpanded ? "expanded" : ""}`}>
                                                        {isExpanded && redeemedPoint.products?.length > 0 && (
                                                            <div className="border-t border-hairline p-4">
                                                                <h4 className="text-title-sm text-ink mb-4">
                                                                    Products Purchased ({redeemedPoint.products.length})
                                                                </h4>

                                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                                    {redeemedPoint.products.map((product, productIndex) => (
                                                                        <div
                                                                            key={productIndex}
                                                                            className="rounded-md product-card overflow-hidden cursor-pointer border border-hairline bg-canvas hover:shadow-card"
                                                                            onClick={() => setSelectedProduct(product)}
                                                                        >
                                                                            <div className="h-40 overflow-hidden bg-surface-soft">
                                                                                <img
                                                                                    src={product.imageUrl}
                                                                                    alt={product.name}
                                                                                    className="w-full h-full object-cover"
                                                                                />
                                                                            </div>

                                                                            <div className="p-4">
                                                                                <h3 className="text-body-md font-medium text-ink mb-1 line-clamp-1">
                                                                                    {product.name}
                                                                                </h3>
                                                                                <p className="text-body-md text-muted line-clamp-2 h-10 mb-2">
                                                                                    {product.description}
                                                                                </p>
                                                                                <div className="flex justify-between items-center">
                                                                                    <span className="text-body-md text-muted">
                                                                                        Qty: {product.quantity}
                                                                                    </span>
                                                                                    <span className="text-body-md font-medium text-ink">
                                                                                        {productPoints[productIndex] > 0 && `${productPoints[productIndex]} pts · `}
                                                                                        {formatPrice(product.price, currency)}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        )}

                        {/* Activity Timeline Tab */}
                        {activeTab === "activity" && (
                            <div>
                                {filteredTransactions.length === 0 ? (
                                    <div className="text-center py-12">
                                        <div className="mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-4 bg-surface-soft">
                                            <HiOutlineClipboardCheck className="h-6 w-6 text-muted" />
                                        </div>
                                        <h3 className="text-title-sm text-ink mb-1">No activity found</h3>
                                        <p className="text-body-md text-muted">
                                            {searchTerm
                                                ? "Try adjusting your search term"
                                                : "This member has no activity yet"}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <div className="absolute top-0 bottom-0 left-6 md:left-1/2 w-px -translate-x-1/2 bg-hairline" />

                                        <div className="space-y-8">
                                            {groupTransactionsByDate().map((group, groupIndex) => (
                                                <div key={groupIndex} className="relative">
                                                    <div className="flex items-center justify-center mb-6 relative z-10">
                                                        <div className="flex items-center justify-center h-12 w-12 rounded-full bg-ink text-onInk">
                                                            <HiOutlineCalendar className="h-6 w-6" />
                                                        </div>
                                                    </div>

                                                    <h3 className="text-center text-title-sm text-ink mb-4">
                                                        {new Date(group.date).toLocaleDateString("en-US", {
                                                            weekday: "long",
                                                            month: "long",
                                                            day: "numeric",
                                                            year: "numeric"
                                                        })}
                                                    </h3>

                                                    <div className="space-y-4 divide-y divide-hairline">
                                                        {group.transactions.map((transaction, transIndex) => {
                                                            const isIntegration = isIntegrationTransaction(transaction);
                                                            const transTime = new Date(transaction.timestamp);

                                                            return (
                                                                <div
                                                                    key={transIndex}
                                                                    className="flex items-start gap-4 md:even:flex-row-reverse pt-4"
                                                                >
                                                                    <div className="min-w-[100px] text-center py-1 px-2 rounded-md text-body-md bg-surface-soft text-muted">
                                                                        {transTime.toLocaleTimeString("en-US", {
                                                                            hour: "2-digit",
                                                                            minute: "2-digit"
                                                                        })}
                                                                    </div>

                                                                    <div className="flex-1 border border-hairline rounded-md p-4 transaction-item bg-canvas">
                                                                        <div className="flex flex-wrap justify-between items-start gap-3 mb-3">
                                                                            <div className="flex items-center gap-2">
                                                                                <div className="h-10 w-10 rounded-full flex items-center justify-center bg-surface-soft text-ink border border-hairline">
                                                                                    {isIntegration ? (
                                                                                        <HiOutlineLink className="h-5 w-5" />
                                                                                    ) : (
                                                                                        <HiOutlineShoppingBag className="h-5 w-5" />
                                                                                    )}
                                                                                </div>
                                                                                <div>
                                                                                    <p className="font-medium text-ink">
                                                                                        {isIntegration ? "Integration" : "Campaign"} Transaction
                                                                                    </p>
                                                                                    <p className="text-body-md text-muted">
                                                                                        +{transaction.value || transaction.points || 0} pts
                                                                                        {isIntegration && transaction.contractNumber && (
                                                                                            <span className="ml-2 font-mono">
                                                                                                #{transaction.contractNumber}
                                                                                            </span>
                                                                                        )}
                                                                                    </p>
                                                                                </div>
                                                                            </div>

                                                                            <Badge variant="default">
                                                                                {getTransactionSourceName(transaction)}
                                                                            </Badge>
                                                                        </div>

                                                                        <div className="text-body-md p-2 rounded-md bg-signature-cream text-ink">
                                                                            +{transaction.value || transaction.points || 0} points earned
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Modal
                isOpen={showEditNameModal}
                onClose={closeEditNameModal}
                title="Edit Member Name"
                size="sm"
                footer={
                    <>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={closeEditNameModal}
                            disabled={isSavingName}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleSaveName}
                            disabled={isSavingName}
                        >
                            {isSavingName ? "Saving..." : "Save"}
                        </Button>
                    </>
                }
            >
                <div className="space-y-3">
                    <TextInput
                        label="Name"
                        type="text"
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        placeholder="Enter member name"
                        autoFocus
                        error={editNameError}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !isSavingName) {
                                e.preventDefault();
                                handleSaveName();
                            }
                        }}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default SingleMemberDetails;
